'use client';

import React, { createContext, useContext, useMemo } from 'react';
import {
  ClassicPdpEntry,
  ClassicPdpEntryData,
} from '@/ui/components/tiles/classic-product-detail/classic-pdp-entry';

type ClassicPdpProductMap = ReadonlyMap<string, ClassicPdpEntryData>;

// Shared empty map, so a consumer outside a provider costs no allocation.
const EMPTY_PRODUCT_MAP: ClassicPdpProductMap = new Map<string, ClassicPdpEntryData>();

const ClassicPdpProductContext = createContext<ClassicPdpProductMap>(EMPTY_PRODUCT_MAP);

export interface ClassicPdpProductProviderProps {
  entries?: ClassicPdpEntry[] | null;
  children: React.ReactNode;
}

/**
 * Publishes the products and bundles that the server resolved for the classic
 * product detail tiles of the page. The tile render path is a client tree, so the tile
 * cannot fetch its own product and looks it up here by the linked path instead.
 */
export function ClassicPdpProductProvider({
  entries,
  children,
}: Readonly<ClassicPdpProductProviderProps>) {
  const productsByPath = useMemo(() => {
    if (!entries || entries.length === 0) {
      return EMPTY_PRODUCT_MAP;
    }

    return new Map(entries.map((entry) => [entry.path, entry.data]));
  }, [entries]);

  return (
    <ClassicPdpProductContext.Provider value={productsByPath}>
      {children}
    </ClassicPdpProductContext.Provider>
  );
}

/**
 * Returns `undefined` when the page resolved nothing for that path. The entry
 * is a product or a bundle; narrow it with the guards of `classic-pdp-entry`.
 */
export function useClassicPdpProduct(path?: string | null): ClassicPdpEntryData | undefined {
  const productsByPath = useContext(ClassicPdpProductContext);

  return path ? productsByPath.get(path) : undefined;
}
